import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import { localDateTimeToISO, toLocalDateInput, toLocalTimeInput } from '../../utils/localDate'
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineVideoCamera,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui'

function extractClientId(selectedCase) {
  if (!selectedCase) return null
  if (selectedCase.client && typeof selectedCase.client === 'object') return selectedCase.client.id
  if (selectedCase.client_id) return selectedCase.client_id
  if (selectedCase.clientId) return selectedCase.clientId
  if (typeof selectedCase.client === 'number' || typeof selectedCase.client === 'string') return selectedCase.client
  return null
}

export default function EditSessionPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [formData, setFormData] = useState({
    title: '', description: '', case: '', client: '',
    date: '', time: '',
  })
  const [originalData, setOriginalData] = useState({})
  const [cases, setCases] = useState([])
  const [clients, setClients] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [casesLoading, setCasesLoading] = useState(true)
  const [clientsLoading, setClientsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
    ;(async () => {
      try {
        // page=all: list endpoints return 10 items per page by default, which left
        // older entries out of this dropdown.
        const response = await apiService.get('/cases/', { params: { ...params, page: 'all' } })
        if (!cancelled && response.success) {
          const data = response.data.data || response.data
          setCases(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setCasesLoading(false)
      }
    })()
    ;(async () => {
      try {
        const response = await apiService.get('/clients/', { params: { ...params, page: 'all' } })
        if (!cancelled && response.success) {
          const data = response.data.data || response.data
          setClients(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setClientsLoading(false)
      }
    })()
    ;(async () => {
      try {
        setPageLoading(true)
        const response = await apiService.get(`/sessions/${id}/`, { params })
        if (cancelled) return
        if (response.success) {
          const sessionData = response.data.data || response.data
          const d = new Date(sessionData.date)
          const data = {
            title: sessionData.title || '',
            description: sessionData.description || '',
            // The API returns case_id / client_id. The nested shapes stay as
            // fallbacks for older payloads.
            case: String(sessionData.case_id || sessionData.case?.id || sessionData.case || ''),
            client: String(sessionData.client_id || sessionData.client?.id || sessionData.client || ''),
            date: toLocalDateInput(d),
            time: toLocalTimeInput(d),
          }
          setFormData(data); setOriginalData(data)
        } else {
          showError(response.error?.message || t('cases.loadFailed', { entity: getEntity('session').singular.toLowerCase() }))
          navigate('/sessions')
        }
      } catch {
        if (!cancelled) {
          showError(t('cases.loadFailed', { entity: getEntity('session').singular.toLowerCase() }))
          navigate('/sessions')
        }
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))

    if (name === 'case' && value) {
      const selectedCase = cases.find(c => c.id === parseInt(value))
      const clientId = extractClientId(selectedCase)
      if (clientId) {
        setFormData(prev => ({ ...prev, client: clientId.toString() }))
        if (errors.client) setErrors(prev => ({ ...prev, client: '' }))
      }
    }
  }

  const validate = () => {
    const next = {}
    if (!formData.title.trim()) next.title = t('cases.create.validationTitleRequired', { entity: getEntity('session').singular })
    if (!formData.case) next.case = t('sessions.create.validationCaseFirst', { caseEntity: getEntity('case').singular.toLowerCase(), clientEntity: getEntity('client').singular.toLowerCase() })
    if (!formData.client) next.client = t('sessions.create.validationCaseFirst', { caseEntity: getEntity('case').singular.toLowerCase(), clientEntity: getEntity('client').singular.toLowerCase() })
    if (!formData.date) next.date = t('cases.create.validationDateRequired', { entity: getEntity('session').singular })
    if (!formData.time) next.time = t('cases.create.validationDateRequired', { entity: getEntity('session').singular })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    if (!hasChanges) {
      showSuccess(t('common.noChangesToSave'))
      return
    }
    try {
      setLoading(true)
      // Send an explicit UTC instant. A bare "2026-10-20T20:30:00" is read
      // by the server as UTC, which moved sessions by the user's offset.
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        case_id: parseInt(formData.case),
        client_id: formData.client ? parseInt(formData.client) : null,
        date: localDateTimeToISO(formData.date, formData.time),
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.put(`/sessions/${id}/`, payload, { params })
      if (response.success) {
        showSuccess(t('documents.updateSuccess', { entity: getEntity('session').singular }))
        navigate(`/sessions/${id}`)
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('documents.updateFailed', { entity: getEntity('session').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.updateFailed', { entity: getEntity('session').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  if (pageLoading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('sessions.detail.backTo', { entity: getEntity('session').singular.toLowerCase() })}
          variant="ghost"
          onClick={() => navigate(`/sessions/${id}`)}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlinePencil}
          iconTone="info"
          title={t('sessions.edit.title', { entity: getEntity('session').singular.toLowerCase() })}
          subtitle={t('sessions.edit.subtitle', { entity: getEntity('session').singular.toLowerCase() })}
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineVideoCamera className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('cases.create.sectionTitle', { entity: getEntity('session').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField label={t('sessions.create.titleLabel', { entity: getEntity('session').singular })} required error={errors.title}>
            <Input
              name="title"
              size="lg"
              value={formData.title}
              onChange={handleChange}
              leadingIcon={HiOutlineVideoCamera}
              placeholder={t('sessions.create.titlePlaceholder')}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('sessions.create.caseLabel', { caseEntity: getEntity('case').singular })} required error={errors.case}>
            <Select
              name="case"
              size="lg"
              value={formData.case}
              onChange={handleChange}
              disabled={loading || casesLoading}
            >
              <option value="">{casesLoading ? t('common.loading') : t('sessions.create.casePlaceholder', { caseEntity: getEntity('case').singular.toLowerCase() })}</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </Select>
          </FormField>

          <FormField label={t('sessions.create.clientLabel', { clientEntity: getEntity('client').singular })} error={errors.client}>
            <Select
              name="client"
              size="lg"
              value={formData.client}
              onChange={handleChange}
              disabled={loading || clientsLoading}
            >
              <option value="">{clientsLoading ? t('common.loading') : t('sessions.create.clientPlaceholder', { clientEntity: getEntity('client').singular.toLowerCase() })}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t('sessions.create.dateLabel')} required error={errors.date}>
              <Input type="date" name="date" size="lg" value={formData.date} onChange={handleChange} leadingIcon={HiOutlineCalendarDays} disabled={loading} />
            </FormField>
            <FormField label={t('sessions.create.timeLabel')} required error={errors.time}>
              <Input type="time" name="time" size="lg" value={formData.time} onChange={handleChange} leadingIcon={HiOutlineClock} disabled={loading} />
            </FormField>
          </div>

          <FormField label={t('sessions.create.descriptionLabel')} error={errors.description}>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder={t('sessions.create.descriptionPlaceholder', { entity: getEntity('session').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          {hasChanges && <Banner tone="info">{t('common.youHaveUnsavedChanges')}</Banner>}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={() => navigate(`/sessions/${id}`)} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
              disabled={!hasChanges}
            >
              {loading ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
